<template>
    <div class="editor-collapsible" :class="{'editor-collapsible--open': isOpen}">
        <!--
            Zugeklappt: Die ganze Vorschaufläche ist EIN Button. Er trägt die
            controlId, solange der Editor nicht da ist - so öffnet auch ein
            Klick auf das Label der Feldhülle das Feld.
        -->
        <button v-if="!isOpen"
                ref="previewButton"
                type="button"
                class="editor-collapsible__preview"
                :id="controlId || undefined"
                aria-expanded="false"
                :aria-controls="regionId"
                :aria-label="editLabel"
                :aria-describedby="previewDescribedBy"
                @click="expand">
            <span :id="previewTextId"
                  class="editor-collapsible__text"
                  :class="{'editor-collapsible__text--empty': isEmpty}">{{ isEmpty ? $t('editor.fields.collapsible.empty') : preview }}</span>
            <span class="editor-collapsible__action" aria-hidden="true">{{ $t('editor.fields.collapsible.edit') }}</span>
        </button>

        <!--
            Aufgeklappt: der bisherige Editor an derselben Stelle. Die Hülle
            bleibt immer im DOM (Ziel von aria-controls), der Editor selbst
            entsteht erst beim Aufklappen.
        -->
        <div :id="regionId" ref="region" class="editor-collapsible__region" :hidden="!isOpen">
            <slot v-if="isOpen" />
        </div>

        <div v-if="isOpen && !forceOpen" class="editor-collapsible__footer">
            <button type="button"
                    class="editor-collapsible__action editor-collapsible__collapse"
                    aria-expanded="true"
                    :aria-controls="regionId"
                    :aria-label="collapseLabel"
                    @click="collapse">{{ $t('editor.fields.collapsible.collapse') }}</button>
        </div>
    </div>
</template>

<script>
// Laufende Nummer für eindeutige ids, auch in Listen ohne controlId.
let instanceCounter = 0;
</script>

<script setup>
/**
 * Langes Textfeld, platzsparend: zugeklappt mit kleiner Vorschau, Aufklappen
 * an Ort und Stelle.
 *
 * Die Komponente kapselt nur Vorschau und Auf-/Zuklappen. Wert, Speichern und
 * Editor bleiben Sache der Feldkomponente, die ihren Editor als Slot
 * hineingibt (TextAreaField: v-textarea, RichTextField: TipTap).
 *
 * Sprung in der App-Vorschau: Der StepEditor hört auf focusin am Dokument und
 * liest data-json-path an der Feldhülle (focusedPathOf). Ein Klick auf die
 * Vorschau fokussiert deshalb zuerst die Vorschau selbst - sie liegt in
 * derselben Hülle - und danach den Editor. Beides löst denselben Sprung aus
 * wie ein Fokus im Feld.
 *
 * @see resources/js/utils/textPreview.mjs
 * @see resources/js/utils/editorPreviewSession.mjs -> focusedPathOf
 */
import {computed, nextTick, ref, watch} from "vue";
import {useI18n} from "@/i18n";
import {textPreview} from "../../utils/textPreview.mjs";

const {t} = useI18n();

const props = defineProps({
    /** Der Feldwert, aus dem die Vorschau entsteht. */
    text: {
        type: [String, Number],
        default: '',
    },
    /** Der Wert ist HTML (RichText) und wird für die Vorschau zu Text. */
    html: {
        type: Boolean,
        default: false,
    },
    /** Feldbeschriftung für die Bildschirmleser-Texte der Knöpfe. */
    label: {
        type: String,
        default: null,
    },
    /** id der Label-Verknüpfung aus der Feldhülle. */
    controlId: {
        type: String,
        default: null,
    },
    /** id des Hilfetexts, damit die Vorschau ihn mitliest. */
    describedBy: {
        type: String,
        default: null,
    },
    /**
     * Das Feld hat einen Fehler: aufgeklappt, damit man ihn sieht und beheben
     * kann. Solange er besteht, lässt sich das Feld nicht zuklappen.
     */
    forceOpen: {
        type: Boolean,
        default: false,
    },
    /**
     * Jede Änderung klappt zu. Für Listen, deren Einträge den Platz tauschen:
     * Der Zustand hängt an der Position, nicht am Eintrag.
     */
    collapseSignal: {
        type: Number,
        default: 0,
    },
});

const uid = ++instanceCounter;
const regionId = computed(() => props.controlId ? `${props.controlId}-editor` : `editor-collapsible-${uid}-editor`);
const previewTextId = computed(() => props.controlId ? `${props.controlId}-preview` : `editor-collapsible-${uid}-preview`);

const previewButton = ref(null);
const region = ref(null);

// Zugeklappt ist der Standard, ein Fehler öffnet.
const open = ref(props.forceOpen);
const isOpen = computed(() => open.value || props.forceOpen);

// Verschwindet der Fehler beim Tippen, bleibt das Feld offen.
watch(() => props.forceOpen, (force) => {
    if (force) open.value = true;
});

watch(() => props.collapseSignal, () => {
    if ( ! props.forceOpen) open.value = false;
});

const preview = computed(() => textPreview(props.text, {html: props.html}));
const isEmpty = computed(() => preview.value === '');

const editLabel = computed(() => props.label
    ? t('editor.fields.collapsible.edit_label', {label: props.label})
    : t('editor.fields.collapsible.edit_label_unnamed'));

const collapseLabel = computed(() => props.label
    ? t('editor.fields.collapsible.collapse_label', {label: props.label})
    : t('editor.fields.collapsible.collapse_label_unnamed'));

const previewDescribedBy = computed(() =>
    [previewTextId.value, props.describedBy].filter(Boolean).join(' ') || undefined);

// Das eigentliche Eingabeelement im Slot. Der Größenmesser von v-textarea
// (auto-grow) ist ebenfalls ein textarea, aber aria-hidden.
const FOCUS_TARGET = 'textarea:not([aria-hidden="true"]), [contenteditable="true"]';

// TipTap hängt seinen Inhalt erst einen Tick nach dem Mounten ein.
const FOCUS_ATTEMPTS = 5;

function nextFrame() {
    return new Promise(resolve => {
        if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
            window.requestAnimationFrame(() => resolve());
        } else {
            setTimeout(resolve, 16);
        }
    });
}

function placeCaretAtEnd(element) {
    if (typeof element.setSelectionRange === 'function' && typeof element.value === 'string') {
        element.setSelectionRange(element.value.length, element.value.length);

        return;
    }

    if ( ! element.isContentEditable || typeof window.getSelection !== 'function') return;

    const range = document.createRange();
    range.selectNodeContents(element);
    range.collapse(false);

    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);
}

async function focusEditor() {
    for (let attempt = 0; attempt < FOCUS_ATTEMPTS; attempt++) {
        await nextTick();

        const target = region.value?.querySelector(FOCUS_TARGET);
        if (target && target.isConnected) {
            target.focus();
            placeCaretAtEnd(target);

            return;
        }

        await nextFrame();
    }
}

async function expand(event) {
    // Safari fokussiert Buttons beim Klick nicht. Ohne Fokus in der Feldhülle
    // gäbe es kein focusin und damit keinen Sprung in der App-Vorschau - etwa
    // bei einem gesperrten Feld, dessen Editor selbst keinen Fokus annimmt.
    event?.currentTarget?.focus?.({preventScroll: true});

    open.value = true;
    await focusEditor();
}

async function collapse() {
    if (props.forceOpen) return;

    open.value = false;
    await nextTick();
    previewButton.value?.focus();
}
</script>

<style scoped>
.editor-collapsible {
    /* In Listen steht das Feld in einer Flexzeile und soll den Platz füllen. */
    flex: 1 1 auto;
    min-width: 0;
}

/* Vorschau: dezente Fläche, Haarlinie, Radius und Schrift wie ein Feld. */
.editor-collapsible__preview {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 2px;
    width: 100%;
    min-height: var(--editor-control-min-height, 48px);
    margin: 0;
    padding: 10px 16px 8px;
    border: 1px solid var(--rc-hairline);
    border-radius: var(--rc-radius-field);
    background: var(--rc-surface-1);
    color: var(--rc-text);
    font-family: var(--rc-font);
    font-size: 16px;
    font-weight: 400;
    letter-spacing: 0.009375em;
    line-height: 1.5;
    text-align: left;
    cursor: pointer;
    transition: border-color .15s ease;
}

.editor-collapsible__preview:hover {
    border-color: var(--rc-primary);
}

.editor-collapsible__preview:focus-visible {
    outline: 2px solid var(--rc-primary);
    outline-offset: 2px;
}

/* Höchstens zwei Zeilen. textPreview kürzt zusätzlich, falls line-clamp fehlt. */
.editor-collapsible__text {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    overflow: hidden;
    overflow-wrap: anywhere;
}

.editor-collapsible__text--empty {
    color: var(--rc-text-secondary);
}

/* "Bearbeiten" / "Einklappen": rechts unten, klein, Aktionsfarbe. */
.editor-collapsible__action {
    align-self: flex-end;
    font-family: var(--rc-font);
    font-size: var(--rc-fs-btn-sm);
    font-weight: 500;
    line-height: 1.4;
    letter-spacing: .02em;
    color: var(--rc-primary);
}

.editor-collapsible__preview:hover .editor-collapsible__action {
    color: var(--rc-primary-hover);
}

.editor-collapsible__footer {
    display: flex;
    justify-content: flex-end;
    margin-top: var(--editor-space-1, 4px);
}

.editor-collapsible__collapse {
    margin: 0 -8px 0 0;
    padding: 4px 8px;
    border: 0;
    border-radius: var(--rc-radius-small);
    background: transparent;
    cursor: pointer;
}

.editor-collapsible__collapse:hover {
    color: var(--rc-primary-hover);
    background: var(--rc-primary-tint);
}

.editor-collapsible__collapse:focus-visible {
    outline: 2px solid var(--rc-primary);
    outline-offset: 2px;
}

/*
 * Mobil: Tippfläche mindestens 40 px hoch. Die Vorschau hat mit 48 px
 * Mindesthöhe ohnehin genug, der kleine Einklappen-Knopf wächst hier.
 */
@media (max-width: 900px) {
    .editor-collapsible__collapse {
        display: inline-flex;
        align-items: center;
        min-height: var(--rc-touch, 40px);
        padding: 0 12px;
        margin-right: -12px;
    }
}
</style>
